import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiEdit3, FiPackage, FiDollarSign, FiPlus } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { productosService, formulacionesService } from '../../services';
import FormulacionIngredientesModal from '../../components/Productos/FormulacionIngredientesModal';
import DuplicarFormulacionModal from '../../components/Productos/DuplicarFormulacionModal';

const ProductoFormulacion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [producto, setProducto] = useState(null);
  const [formulacion, setFormulacion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDuplicarModalOpen, setIsDuplicarModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Función para traducir mensajes de error
  const translateError = (errorMessage) => {
    if (!errorMessage) return errorMessage;

    // Traducir errores de validación de fórmulas
    if (errorMessage.includes('Formula validation failed:')) {
      let translatedMsg = errorMessage;

      if (errorMessage.includes('Capsule type must be specified for capsule containers')) {
        translatedMsg = translatedMsg.replace(
          'Capsule type must be specified for capsule containers',
          'Debe especificar el tipo de cápsula para envases de cápsulas'
        );
      }

      if (errorMessage.includes('Container type must be specified')) {
        translatedMsg = translatedMsg.replace(
          'Container type must be specified',
          'Debe especificar el tipo de envase'
        );
      }

      if (errorMessage.includes('Total percentage must equal 100%')) {
        translatedMsg = translatedMsg.replace(
          'Total percentage must equal 100%',
          'El porcentaje total debe ser igual a 100%'
        );
      }

      if (errorMessage.includes('At least one ingredient is required')) {
        translatedMsg = translatedMsg.replace(
          'At least one ingredient is required',
          'Se requiere al menos un ingrediente'
        );
      }

      // Reemplazar el prefijo
      translatedMsg = translatedMsg.replace('Formula validation failed:', 'Error de validación de la fórmula:');

      return translatedMsg;
    }

    // Otros errores generales
    if (errorMessage.includes('Bad Request')) {
      return 'Solicitud incorrecta. Verifique los datos ingresados.';
    }

    return errorMessage;
  };

  // Estados para el formulario de creación/edición
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(null);

  // Estados para agregar ingrediente inline
  const [showIngredientForm, setShowIngredientForm] = useState(false);
  const [newIngredient, setNewIngredient] = useState({
    ingredient_id: '',
    ingredient_name: '',
    quantity_per_unit: '',
    unit: 'MG'
  });
  const [availableIngredients, setAvailableIngredients] = useState([]);

  // Verificar si viene de crear un producto nuevo
  useEffect(() => {
    if (location.state?.message && location.state?.isNewProduct) {
      setSuccessMessage(location.state.message);
      // Limpiar el state para que no se muestre de nuevo al navegar
      window.history.replaceState({}, document.title);
      // Auto-ocultar después de 8 segundos
      const timer = setTimeout(() => setSuccessMessage(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    loadProductoYFormulacion();
    loadAvailableIngredients();
  }, [id]);

  const loadAvailableIngredients = async () => {
    try {
      console.log('Cargando ingredientes disponibles...');
      const result = await productosService.getProductos({ product_type: 'INGREDIENT' });
      console.log('Resultado de getProductos:', result);

      if (result.success && result.data) {
        const ingredientes = result.data.productos || result.data || [];
        console.log('Ingredientes cargados:', ingredientes);
        console.log('Cantidad de ingredientes:', ingredientes.length);
        setAvailableIngredients(ingredientes);
      } else {
        console.log('No se pudieron cargar ingredientes, intentando sin filtro...');
        // Intentar cargar todos los productos y filtrar localmente
        const allResult = await productosService.getProductos({});
        if (allResult.success && allResult.data) {
          const allProducts = allResult.data.productos || allResult.data || [];
          const ingredientes = allProducts.filter(p => p.product_type === 'INGREDIENT');
          console.log('Ingredientes filtrados localmente:', ingredientes.length);
          setAvailableIngredients(ingredientes);
        }
      }
    } catch (error) {
      console.error('Error cargando ingredientes:', error);
    }
  };

  const handleAddIngredient = () => {
    if (!newIngredient.ingredient_id || !newIngredient.quantity_per_unit) {
      setError('Debe seleccionar un ingrediente y especificar la cantidad');
      return;
    }

    const selectedIngredient = availableIngredients.find(
      ing => ing.id === parseInt(newIngredient.ingredient_id)
    );

    if (!selectedIngredient) {
      setError('Ingrediente no encontrado');
      return;
    }

    const ingredientToAdd = {
      ingredient_id: selectedIngredient.id,
      ingredient_name: selectedIngredient.name,
      quantity_per_unit: parseFloat(newIngredient.quantity_per_unit),
      unit: newIngredient.unit
    };

    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, ingredientToAdd]
    });

    // Limpiar formulario
    setNewIngredient({
      ingredient_id: '',
      ingredient_name: '',
      quantity_per_unit: '',
      unit: 'MG'
    });
    setShowIngredientForm(false);
    setError(null);
  };

  const loadProductoYFormulacion = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Cargar datos del producto
      const resultProducto = await productosService.getProducto(id);
      if (resultProducto.success) {
        setProducto(resultProducto.data);

        console.log('===== DIAGNÓSTICO DE FORMULACIÓN =====');
        console.log('Nombre del producto:', resultProducto.data.name);
        console.log('ID del producto:', resultProducto.data.id);
        console.log('Notas del producto:', resultProducto.data.notes);

        // Intentar extraer el Formula ID de las notas del producto
        let formulaId = null;
        if (resultProducto.data.notes) {
          const match = resultProducto.data.notes.match(/formula ID: (\d+)/i);
          if (match) {
            formulaId = parseInt(match[1]);
            console.log('Formula ID extraído de notas:', formulaId);
          }
        }

        if (formulaId) {
          // Obtener la formulación COMPLETA directamente por ID
          const resultFormulacionCompleta = await formulacionesService.getFormulacion(formulaId);

          console.log('Formulación completa obtenida:', resultFormulacionCompleta);

          if (resultFormulacionCompleta.success && resultFormulacionCompleta.data) {
            const formulacionActiva = resultFormulacionCompleta.data;
            console.log('Formulación activa:', formulacionActiva);
            console.log('ID de formulación:', formulacionActiva.id);
            console.log('Estructura completa de formulación:', JSON.stringify(formulacionActiva, null, 2));
            setFormulacion(formulacionActiva);

            // Cargar los datos en el formulario para edición directa
            const ingredientesFormateados = formulacionActiva.ingredients || [];
            console.log('Ingredientes encontrados:', ingredientesFormateados);
            console.log('Cantidad de ingredientes:', ingredientesFormateados.length);

            setFormData({
              ...formulacionActiva,
              // Convertir nulls a strings vacíos para evitar warnings de React
              source_formula_name: formulacionActiva.source_formula_name || '',
              lid_type: formulacionActiva.lid_type || '',
              mixing_time_minutes: formulacionActiva.mixing_time_minutes || '',
              best_use_date_years: formulacionActiva.best_use_date_years || '',
              label_printer: formulacionActiva.label_printer || '',
              box_size: formulacionActiva.box_size || '',
              pieces_per_box: formulacionActiva.pieces_per_box || '',
              notes: formulacionActiva.notes || '',
              // Parsear quantity_per_container para quitar el formato decimal
              quantity_per_container: formulacionActiva.quantity_per_container ? parseFloat(formulacionActiva.quantity_per_container) : '',
              ingredients: ingredientesFormateados,
              production_quantities: formulacionActiva.production_quantities?.map(pq => pq.quantity) || [250, 500, 1000, 2000]
            });
            setIsEditing(true); // Siempre en modo edición

            console.log('formData después de cargar:', {
              ingredients: ingredientesFormateados,
              totalIngredients: ingredientesFormateados.length
            });
          } else {
            console.log('No se pudo obtener la formulación completa');
            setFormulacion(null);
            initializeFormulacionForm(resultProducto.data);
          }
        } else {
          console.log('No se encontró Formula ID en las notas del producto');
          setFormulacion(null);
          initializeFormulacionForm(resultProducto.data);
        }
      } else {
        setError('No se pudo cargar el producto');
      }
    } catch (error) {
      console.error('Error cargando formulación:', error);
      setError('Error de conexión al cargar la información');
    } finally {
      setIsLoading(false);
    }
  };

  const initializeFormulacionForm = (productoData) => {
    setFormData({
      company_name: 'BETEL NATURAL',
      formula_type: 'BETEL',
      formula_name: productoData.name, // Inicializar con el nombre del producto
      container_type: 'CAPSULES',
      lid_type: '',
      quantity_per_container: '',
      quantity_unit: 'CAPS',
      capsule_type: 'VEGETAL',
      mixing_time_minutes: '',
      best_use_date_years: '',
      label_printer: '',
      box_size: '',
      pieces_per_box: '',
      includes_silica: false,
      ingredients: [],
      production_quantities: [250, 500, 1000, 2000]
    });
    setIsEditing(true); // Siempre en modo edición
  };

  // Manejar la selección de formulación para duplicar
  const handleSelectFormulacion = (formulacionSeleccionada) => {
    console.log('Formulación seleccionada para duplicar:', formulacionSeleccionada);

    // Cargar todos los datos de la formulación seleccionada en el formulario actual
    setFormData({
      company_name: formulacionSeleccionada.company_name || 'BETEL NATURAL',
      formula_type: formulacionSeleccionada.formula_type || 'BETEL',
      formula_name: formulacionSeleccionada.formula_name || producto?.name || '',
      container_type: formulacionSeleccionada.container_type || '',
      lid_type: formulacionSeleccionada.lid_type || '',
      quantity_per_container: formulacionSeleccionada.quantity_per_container ? parseFloat(formulacionSeleccionada.quantity_per_container) : '',
      quantity_unit: formulacionSeleccionada.quantity_unit || 'CAPS',
      capsule_type: formulacionSeleccionada.capsule_type || 'VEGETAL',
      mixing_time_minutes: formulacionSeleccionada.mixing_time_minutes || '',
      best_use_date_years: formulacionSeleccionada.best_use_date_years || '',
      label_printer: formulacionSeleccionada.label_printer || '',
      box_size: formulacionSeleccionada.box_size || '',
      pieces_per_box: formulacionSeleccionada.pieces_per_box || '',
      includes_silica: formulacionSeleccionada.includes_silica || false,
      ingredients: formulacionSeleccionada.ingredients || [],
      production_quantities: formulacionSeleccionada.production_quantities?.map(pq => pq.quantity) || [250, 500, 1000, 2000]
    });

    // Mostrar mensaje de éxito
    setSuccessMessage(`Formulación duplicada exitosamente. Revisa los datos y haz clic en FINALIZAR para guardar.`);
    setTimeout(() => setSuccessMessage(null), 8000);
  };

  const handleGenerarEstimado = async () => {
    if (!formulacion || !formulacion.id) {
      setError('Primero debes guardar la formulación antes de generar un estimado');
      return;
    }

    try {
      // Solicitar cantidad de producción al usuario
      const cantidadStr = window.prompt('¿Para cuántas unidades deseas calcular el costo?', '1000');

      if (!cantidadStr) return; // Usuario canceló

      const cantidad = parseInt(cantidadStr);
      if (isNaN(cantidad) || cantidad <= 0) {
        setError('La cantidad debe ser un número mayor a 0');
        return;
      }

      setIsSaving(true);

      console.log('Generando estimado para formulación ID:', formulacion.id);
      console.log('Cantidad de producción:', cantidad);

      // Llamar al endpoint de cálculo
      const response = await formulacionesService.calcularCostoFormulacion(formulacion.id, {
        production_quantity: cantidad,
        include_labor_cost: true,
        overhead_percentage: 15 // 15% de overhead por defecto
      });

      console.log('Respuesta del cálculo:', response);

      if (response.success && response.data) {
        const calculo = response.data;

        // Generar tirilla de impresión
        generarTirillaEstimado(calculo, producto, formData);
      } else {
        const errorMsg = response.message || 'Error al calcular el estimado';
        console.error('Error en la respuesta:', errorMsg);
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Error generando estimado completo:', error);
      console.error('Detalles del error:', error.response?.data);

      let errorMessage = 'Error al generar el estimado';

      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      alert('❌ ' + errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const generarTirillaEstimado = (calculo, producto, formData) => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    const fecha = new Date().toLocaleString('es-GT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Estimado de Producción - ${producto.name}</title>
        <style>
          @media print {
            @page {
              size: 80mm auto;
              margin: 5mm;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .no-print {
              display: none;
            }
          }

          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            padding: 10px;
            max-width: 80mm;
            margin: 0 auto;
          }

          .header {
            text-align: center;
            border-bottom: 2px dashed #000;
            padding-bottom: 10px;
            margin-bottom: 10px;
          }

          .header h1 {
            font-size: 16px;
            margin: 0 0 5px 0;
            font-weight: bold;
          }

          .header p {
            margin: 2px 0;
            font-size: 11px;
          }

          .section {
            margin-bottom: 10px;
            padding-bottom: 10px;
            border-bottom: 1px dashed #ccc;
          }

          .section-title {
            font-weight: bold;
            font-size: 13px;
            margin-bottom: 5px;
            text-transform: uppercase;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            margin: 3px 0;
          }

          .info-label {
            font-weight: bold;
          }

          .ingredients-table {
            width: 100%;
            margin-top: 5px;
          }

          .ingredients-table th {
            text-align: left;
            border-bottom: 1px solid #000;
            padding: 3px 0;
            font-size: 11px;
          }

          .ingredients-table td {
            padding: 3px 0;
            font-size: 11px;
          }

          .text-right {
            text-align: right;
          }

          .total-section {
            margin-top: 10px;
            padding-top: 10px;
            border-top: 2px solid #000;
          }

          .total-row {
            display: flex;
            justify-content: space-between;
            margin: 3px 0;
            font-size: 13px;
          }

          .total-row.grand-total {
            font-weight: bold;
            font-size: 14px;
            border-top: 1px solid #000;
            border-bottom: 2px double #000;
            padding: 5px 0;
            margin-top: 5px;
          }

          .footer {
            text-align: center;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 2px dashed #000;
            font-size: 10px;
          }

          .print-button {
            display: block;
            width: 200px;
            margin: 20px auto;
            padding: 10px 20px;
            background: #7B2D8E;
            color: white;
            border: none;
            border-radius: 5px;
            font-size: 14px;
            cursor: pointer;
          }

          .print-button:hover {
            background: #6a2578;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>BETEL NATURAL</h1>
          <p>ESTIMADO DE PRODUCCIÓN</p>
          <p>${fecha}</p>
        </div>

        <div class="section">
          <div class="section-title">Producto</div>
          <div class="info-row">
            <span class="info-label">Nombre:</span>
            <span>${producto.name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Fórmula:</span>
            <span>${formData.formula_name || producto.name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Compañía:</span>
            <span>${formData.company_name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Tipo:</span>
            <span>${formData.container_type === 'CAPSULES' ? 'Cápsulas' : formData.container_type}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Cantidad/Envase:</span>
            <span>${formData.quantity_per_container} ${formData.quantity_unit}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Producción</div>
          <div class="info-row">
            <span class="info-label">Cantidad a producir:</span>
            <span>${calculo.production_quantity} unidades</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Ingredientes</div>
          <table class="ingredients-table">
            <thead>
              <tr>
                <th>Ingrediente</th>
                <th class="text-right">Cant.</th>
                <th class="text-right">Costo</th>
              </tr>
            </thead>
            <tbody>
              ${calculo.ingredient_costs?.map(ing => `
                <tr>
                  <td>${ing.ingredient_name}</td>
                  <td class="text-right">${parseFloat(ing.total_quantity || 0).toFixed(2)} ${ing.unit}</td>
                  <td class="text-right">$${parseFloat(ing.total_cost || 0).toFixed(2)}</td>
                </tr>
              `).join('') || '<tr><td colspan="3">No hay ingredientes</td></tr>'}
            </tbody>
          </table>
        </div>

        <div class="total-section">
          <div class="total-row">
            <span>Subtotal Ingredientes:</span>
            <span>$${parseFloat(calculo.total_ingredient_cost || 0).toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Empaque/Sílica:</span>
            <span>$${parseFloat(calculo.packaging_cost || 0).toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Mano de Obra:</span>
            <span>$${parseFloat(calculo.labor_cost || 0).toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Overhead (15%):</span>
            <span>$${parseFloat(calculo.overhead_cost || 0).toFixed(2)}</span>
          </div>
          <div class="total-row grand-total">
            <span>TOTAL:</span>
            <span>$${parseFloat(calculo.total_cost || 0).toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Costo por Unidad:</span>
            <span>$${parseFloat(calculo.cost_per_unit || 0).toFixed(2)}</span>
          </div>
        </div>

        <div class="footer">
          <p>Documento generado automáticamente</p>
          <p>Betel Natural - ${fecha}</p>
        </div>

        <button class="print-button no-print" onclick="window.print()">
          🖨️ IMPRIMIR
        </button>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();

    // Auto-imprimir después de cargar (opcional)
    // printWindow.onload = () => {
    //   printWindow.print();
    // };
  };

  const handleSaveFormulacion = async () => {
    setError(null);

    // Validaciones
    if (!formData.company_name) {
      setError('El nombre de la compañía es requerido');
      return;
    }
    if (!formData.quantity_per_container || formData.quantity_per_container <= 0) {
      setError('La cantidad por envase debe ser mayor a 0');
      return;
    }
    if (!formData.ingredients || formData.ingredients.length === 0) {
      setError('Debe agregar al menos un ingrediente');
      return;
    }

    setIsSaving(true);

    try {
      const formulaData = {
        company_name: formData.company_name,
        formula_type: formData.formula_type || 'BETEL',
        formula_name: formData.formula_name,
        container_type: formData.container_type || 'CAPSULES', // OBLIGATORIO
        lid_type: formData.lid_type || null,
        quantity_per_container: parseFloat(formData.quantity_per_container),
        quantity_unit: formData.quantity_unit || 'CAPS',
        capsule_type: formData.capsule_type || 'VEGETAL',
        mixing_time_minutes: formData.mixing_time_minutes ? parseInt(formData.mixing_time_minutes) : null,
        best_use_date_years: formData.best_use_date_years ? parseInt(formData.best_use_date_years) : null,
        label_printer: formData.label_printer || null,
        box_size: formData.box_size || null,
        pieces_per_box: formData.pieces_per_box ? parseInt(formData.pieces_per_box) : null,
        includes_silica: formData.includes_silica || false,
        ingredients: formData.ingredients.map(ing => ({
          ingredient_id: parseInt(ing.ingredient_id),
          ingredient_name: ing.ingredient_name,
          quantity_per_unit: parseFloat(ing.quantity_per_unit),
          unit: ing.unit,
          percentage: ing.percentage ? parseFloat(ing.percentage) : null,
          notes: ing.notes || null
        })),
        production_quantities: formData.production_quantities.map(q => ({ quantity: parseInt(q) }))
      };

      console.log('Datos de formulación a enviar:', JSON.stringify(formulaData, null, 2));

      let result;

      // Verificar si existe una formulación y tiene un ID válido
      const formulacionId = formulacion?.id || formulacion?.formula_id || formulacion?.formulation_id;

      console.log('Estado de formulación:', {
        existeFormulacion: !!formulacion,
        formulacionCompleta: formulacion,
        idEncontrado: formulacionId,
        tipoDeId: typeof formulacionId
      });

      if (formulacionId) {
        // Actualizar formulación existente usando el endpoint de formulas directamente
        console.log('Actualizando formulación ID:', formulacionId);
        result = await formulacionesService.updateFormulacion(formulacionId, formulaData);
      } else {
        // Crear nueva formulación usando el endpoint de formulas
        console.log('Creando nueva formulación para producto:', producto.name);
        result = await formulacionesService.createFormulacion(formulaData);
      }

      if (result.success) {
        // Mostrar mensaje de éxito
        alert('Formulación guardada exitosamente');

        // Redirigir a la página del producto
        navigate(`/system/productos/${id}`);
      } else {
        // Traducir el mensaje de error antes de mostrarlo
        const translatedError = translateError(result.message || 'Error al guardar la formulación');
        setError(translatedError);
      }
    } catch (error) {
      console.error('Error guardando formulación:', error);
      setError('Error al guardar la formulación');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={4} message="Cargando formulación del producto..." />;
  }

  if (!producto) {
    return (
      <div className="error-container">
        <h2>Producto no encontrado</h2>
        <button onClick={() => navigate('/system/productos')} className="btn btn--primary">
          Volver a Productos
        </button>
      </div>
    );
  }

  const productTabs = [
    { id: 'datos', label: 'Datos Producto', route: '' },
    { id: 'ventas', label: 'Ventas', route: '/ventas' },
    { id: 'inventario', label: 'Inventario', route: '/inventario' },
    { id: 'formulacion', label: 'Formulación', route: '/formulacion' }
  ];

  return (
    <div className="cliente-detalle-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/productos')}
          type="button"
        >
          <FiArrowLeft /> Volver a Productos
        </button>

        <h1 className="form-title">
          FORMULACIÓN
        </h1>

        <div className="form-actions">
          <button
            className="btn btn--primary"
            onClick={() => navigate(`/system/productos/${id}/editar`)}
            type="button"
          >
            <FiEdit3 /> Editar Producto
          </button>
        </div>
      </div>

      {/* Contenido */}
      <div className="cliente-detail-form">
        {/* Mensaje de éxito cuando viene de crear producto */}
        {successMessage && (
          <div className="alert alert--success" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{successMessage}</span>
              <button
                onClick={() => setSuccessMessage(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert--danger">
            {error}
          </div>
        )}

        {(!formulacion || isEditing) && formData ? (
          /* FORMULARIO DE CREACIÓN/EDICIÓN */
          <div className="formulacion-form-container">
            {/* Header con selectores de fórmula */}
            <div className="formulacion-header" style={{
              marginBottom: '24px',
              padding: '16px 20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ flex: '0 0 200px' }}>
                  <label style={{ display: 'block', fontSize: '0.85em', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                    FÓRMULA
                  </label>
                  <select
                    className="form-input"
                    value={formData.formula_type}
                    onChange={(e) => setFormData({ ...formData, formula_type: e.target.value })}
                    style={{ width: '100%' }}
                  >
                    <option value="BETEL">BETEL</option>
                    <option value="EXCLUSIVA">EXCLUSIVA</option>
                  </select>
                </div>

                <div style={{ flex: '1' }}>
                  <label style={{ display: 'block', fontSize: '0.85em', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                    NOMBRE
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.formula_name || ''}
                    onChange={(e) => setFormData({ ...formData, formula_name: e.target.value })}
                    placeholder="Nombre de la fórmula"
                    style={{ width: '100%' }}
                  />
                </div>

                <div style={{ flex: '0 0 auto', paddingTop: '24px' }}>
                  <button
                    className="btn btn--outline"
                    onClick={() => setIsDuplicarModalOpen(true)}
                    type="button"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    DUPLICAR
                  </button>
                </div>
              </div>
            </div>

            <div className="form-header-inline" style={{ marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: 0 }}>{isEditing ? 'Editar Formulación' : 'Crear Nueva Formulación'}</h3>
                <p style={{ color: '#666', margin: '8px 0 0 0' }}>
                  Complete todos los campos requeridos para la formulación del producto
                </p>
              </div>
            </div>

            {/* Formulario de campos en formato horizontal/grid */}
            <div className="formulacion-fields-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '16px',
              marginBottom: '32px',
              padding: '20px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div className="form-group">
                <label className="form-label">Compañía</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="BETEL NATURAL"
                  list="company-names-list"
                  required
                />
                <datalist id="company-names-list">
                  <option value="BETEL NATURAL" />
                  <option value="Natural&Lab" />
                  <option value="Natural and Lab" />
                </datalist>
              </div>

              <div className="form-group">
                <label className="form-label">Nombre de Producto</label>
                <input
                  type="text"
                  className="form-input"
                  value={producto?.name || ''}
                  disabled
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Tipo de Envase</label>
                <select
                  className="form-input"
                  value={formData.container_type}
                  onChange={(e) => setFormData({ ...formData, container_type: e.target.value })}
                  required
                >
                  <option value="CAPSULES">Cápsulas</option>
                  <option value="POWDERS">Polvos</option>
                  <option value="OILS">Aceites</option>
                  <option value="TEAS">Tés</option>
                  <option value="LEAVES">Hojas</option>
                  <option value="LIQUIDS">Líquidos</option>
                  <option value="CREAMS">Cremas</option>
                  <option value="OTHER">Otro</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Tapadera</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.lid_type}
                  onChange={(e) => setFormData({ ...formData, lid_type: e.target.value })}
                  placeholder="SPRAY GOLD"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Cantidad por Envase</label>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.quantity_per_container}
                    onChange={(e) => setFormData({ ...formData, quantity_per_container: e.target.value })}
                    placeholder="90"
                    min="0"
                    step="1"
                    required
                  />
                  <select
                    className="form-input"
                    value={formData.quantity_unit}
                    onChange={(e) => setFormData({ ...formData, quantity_unit: e.target.value })}
                  >
                    <option value="CAPS">CAPS</option>
                    <option value="UNIT">Unidades</option>
                    <option value="G">Gramos</option>
                    <option value="ML">Mililitros</option>
                  </select>
                </div>
              </div>

              {formData.container_type === 'CAPSULES' && (
                <div className="form-group">
                  <label className="form-label">Tipo de Cápsulas</label>
                  <select
                    className="form-input"
                    value={formData.capsule_type}
                    onChange={(e) => setFormData({ ...formData, capsule_type: e.target.value })}
                  >
                    <option value="VEGETAL">VEGETAL</option>
                    <option value="GELATINA">GELATINA</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Tiempo para Mezclar</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.mixing_time_minutes}
                  onChange={(e) => setFormData({ ...formData, mixing_time_minutes: e.target.value })}
                  placeholder="20 MINUTOS"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Fecha de Mejor Uso</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.best_use_date_years}
                  onChange={(e) => setFormData({ ...formData, best_use_date_years: e.target.value })}
                  placeholder="5 AÑOS"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Etiqueta de Impresión o Envase</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.label_printer}
                  onChange={(e) => setFormData({ ...formData, label_printer: e.target.value })}
                  placeholder="EN BETEL"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Medida de Caja para Empacar</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.box_size}
                  onChange={(e) => setFormData({ ...formData, box_size: e.target.value })}
                  placeholder="16X13X13"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Piezas por Caja</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.pieces_per_box}
                  onChange={(e) => setFormData({ ...formData, pieces_per_box: e.target.value })}
                  placeholder="105"
                  min="0"
                  step="1"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Silica</label>
                <select
                  className="form-input"
                  value={formData.includes_silica ? 'SI' : 'NO'}
                  onChange={(e) => setFormData({ ...formData, includes_silica: e.target.value === 'SI' })}
                >
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </div>
            </div>

            {/* Sección de Ingredientes - Abajo del formulario */}
            <div className="ingredients-section" style={{
              marginBottom: '32px',
              padding: '20px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>INGREDIENTES</h2>
              </div>

              <div className="ingredients-table-wrapper" style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  border: '1px solid #ddd'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th rowSpan="2" style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #ddd',
                        borderRight: '1px solid #ddd',
                        fontWeight: '600',
                        minWidth: '200px'
                      }}>
                        INGREDIENTES
                      </th>
                      <th rowSpan="2" style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid #ddd',
                        borderRight: '1px solid #ddd',
                        fontWeight: '600',
                        minWidth: '150px'
                      }}>
                        Cantidad de<br/>ingredientes por
                      </th>
                      <th colSpan="4" style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '1px solid #ddd',
                        fontWeight: '600'
                      }}>
                        Cantidad de producción
                      </th>
                      <th rowSpan="2" style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid #ddd',
                        borderLeft: '1px solid #ddd',
                        fontWeight: '600',
                        minWidth: '80px'
                      }}>
                        Acciones
                      </th>
                    </tr>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid #ddd',
                        borderRight: '1px solid #ddd',
                        fontWeight: '600'
                      }}>250</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid #ddd',
                        borderRight: '1px solid #ddd',
                        fontWeight: '600'
                      }}>500</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid #ddd',
                        borderRight: '1px solid #ddd',
                        fontWeight: '600'
                      }}>1000</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '2px solid #ddd',
                        borderRight: '1px solid #ddd',
                        fontWeight: '600'
                      }}>2000</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.ingredients.map((ing, idx) => {
                      const quantityPerUnit = parseFloat(ing.quantity_per_unit || ing.quantity || 0);
                      const containerQty = parseFloat(formData.quantity_per_container || 1);

                      const calc250 = (quantityPerUnit * 250) / containerQty;
                      const calc500 = (quantityPerUnit * 500) / containerQty;
                      const calc1000 = (quantityPerUnit * 1000) / containerQty;
                      const calc2000 = (quantityPerUnit * 2000) / containerQty;

                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #ddd' }}>
                          <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>
                            {ing.ingredientName || ing.ingredient_name || 'N/A'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                            {quantityPerUnit.toFixed(1)} {ing.unit || 'mg'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                            {calc250.toFixed(1)} g
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                            {calc500.toFixed(1)} g
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                            {calc1000.toFixed(1)} g
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                            {calc2000.toFixed(1)} g
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center', borderLeft: '1px solid #ddd' }}>
                            <button
                              onClick={() => {
                                const newIngredients = formData.ingredients.filter((_, i) => i !== idx);
                                setFormData({ ...formData, ingredients: newIngredients });
                              }}
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                              }}
                              type="button"
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Fila de formulario para agregar ingrediente */}
                    {showIngredientForm && (() => {
                      console.log('Renderizando formulario de ingredientes');
                      console.log('availableIngredients:', availableIngredients);
                      console.log('Cantidad:', availableIngredients.length);
                      return (
                        <tr style={{ backgroundColor: '#f0f8ff', borderTop: '2px solid #7B2D8E' }}>
                          <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>
                            <select
                              className="form-input"
                              value={newIngredient.ingredient_id}
                              onChange={(e) => {
                                console.log('Ingrediente seleccionado:', e.target.value);
                                setNewIngredient({ ...newIngredient, ingredient_id: e.target.value });
                              }}
                              style={{ width: '100%', padding: '8px' }}
                            >
                              <option value="">Seleccione ingrediente... ({availableIngredients.length} disponibles)</option>
                              {availableIngredients.map(ing => {
                                console.log('Mapeando ingrediente:', ing);
                                return (
                                  <option key={ing.id} value={ing.id}>{ing.name}</option>
                                );
                              })}
                            </select>
                          </td>
                        <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="number"
                              className="form-input"
                              value={newIngredient.quantity_per_unit}
                              onChange={(e) => setNewIngredient({ ...newIngredient, quantity_per_unit: e.target.value })}
                              placeholder="8.6"
                              step="0.1"
                              min="0"
                              style={{ width: '80px', padding: '8px' }}
                            />
                            <select
                              className="form-input"
                              value={newIngredient.unit}
                              onChange={(e) => setNewIngredient({ ...newIngredient, unit: e.target.value })}
                              style={{ width: '70px', padding: '8px' }}
                            >
                              <option value="MG">mg</option>
                              <option value="G">g</option>
                              <option value="KG">kg</option>
                              <option value="ML">ml</option>
                              <option value="L">l</option>
                              <option value="MCG">mcg</option>
                              <option value="UNIT">unidad</option>
                            </select>
                          </div>
                        </td>
                        <td colSpan="4" style={{ padding: '12px', borderRight: '1px solid #ddd', textAlign: 'center', color: '#888' }}>
                          Las cantidades se calcularán automáticamente
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', borderLeft: '1px solid #ddd' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button
                              onClick={handleAddIngredient}
                              style={{
                                background: '#28a745',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                              }}
                              type="button"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => {
                                setShowIngredientForm(false);
                                setNewIngredient({ ingredient_id: '', ingredient_name: '', quantity_per_unit: '', unit: 'MG' });
                              }}
                              style={{
                                background: '#6c757d',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                              }}
                              type="button"
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })()}

                    <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold', borderTop: '2px solid #333' }}>
                      <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #ddd' }}>
                        Total:
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                        {formData.ingredients.reduce((sum, ing) => {
                          return sum + parseFloat(ing.quantity_per_unit || ing.quantity || 0);
                        }, 0).toFixed(1)} mg
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                        {formData.ingredients.reduce((sum, ing) => {
                          const qty = parseFloat(ing.quantity_per_unit || ing.quantity || 0);
                          const container = parseFloat(formData.quantity_per_container || 1);
                          return sum + (qty * 250) / container;
                        }, 0).toFixed(1)} g
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                        {formData.ingredients.reduce((sum, ing) => {
                          const qty = parseFloat(ing.quantity_per_unit || ing.quantity || 0);
                          const container = parseFloat(formData.quantity_per_container || 1);
                          return sum + (qty * 500) / container;
                        }, 0).toFixed(1)} g
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                        {formData.ingredients.reduce((sum, ing) => {
                          const qty = parseFloat(ing.quantity_per_unit || ing.quantity || 0);
                          const container = parseFloat(formData.quantity_per_container || 1);
                          return sum + (qty * 1000) / container;
                        }, 0).toFixed(1)} g
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                        {formData.ingredients.reduce((sum, ing) => {
                          const qty = parseFloat(ing.quantity_per_unit || ing.quantity || 0);
                          const container = parseFloat(formData.quantity_per_container || 1);
                          return sum + (qty * 2000) / container;
                        }, 0).toFixed(1)} g
                      </td>
                      <td style={{ borderLeft: '1px solid #ddd' }}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Botones de acción debajo de todo */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '1px solid #e0e0e0'
            }}>
              <button
                className="btn btn--primary"
                onClick={() => setShowIngredientForm(!showIngredientForm)}
                type="button"
                disabled={showIngredientForm}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: showIngredientForm ? '#ccc' : '#7B2D8E',
                  borderColor: showIngredientForm ? '#ccc' : '#7B2D8E'
                }}
              >
                <FiPlus /> AGREGAR INGREDIENTE
              </button>

              <button
                className="btn btn--outline"
                onClick={handleGenerarEstimado}
                disabled={isSaving || !formulacion}
                type="button"
                style={{
                  backgroundColor: '#fff',
                  color: '#7B2D8E',
                  border: '2px solid #7B2D8E'
                }}
              >
                GENERAR ESTIMADO
              </button>

              <button
                className="btn btn--primary"
                onClick={handleSaveFormulacion}
                disabled={isSaving}
                type="button"
                style={{
                  backgroundColor: '#D4AF37',
                  borderColor: '#D4AF37',
                  color: '#333'
                }}
              >
                {isSaving ? 'Guardando...' : 'FINALIZAR'}
              </button>
            </div>
          </div>
        ) : (
          /* NO HAY FORMULACIÓN Y NO HAY FORMDATA */
          <div className="empty-state" style={{ padding: '32px', textAlign: 'center' }}>
            <p>No se pudo cargar la información de la formulación</p>
          </div>
        )}
      </div>

      {/* Menú de navegación inferior */}
      <BottomTabMenu
        baseRoute={`/system/productos/${id}`}
        tabs={productTabs}
        currentTab="formulacion"
      />

      {/* Modal para configurar ingredientes - Comentado, ahora se usa formulario inline */}
      {/* {formData && (
        <FormulacionIngredientesModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          producto={producto}
          initialIngredients={formData.ingredients}
          onSave={(ingredientes) => {
            setFormData({
              ...formData,
              ingredients: ingredientes.map(ing => ({
                ingredient_id: ing.ingredient_id,
                ingredient_name: ing.ingredientName || ing.ingredient_name,
                quantity_per_unit: ing.quantity,
                unit: ing.unit,
                percentage: ing.percentage || null,
                notes: ing.notes || null
              }))
            });
            setIsModalOpen(false);
          }}
          onSuccess={() => {
            if (formulacion) {
              loadProductoYFormulacion();
            }
          }}
        />
      )} */}

      {/* Modal para duplicar formulación */}
      <DuplicarFormulacionModal
        isOpen={isDuplicarModalOpen}
        onClose={() => setIsDuplicarModalOpen(false)}
        onSelectFormulacion={handleSelectFormulacion}
      />
    </div>
  );
};

export default ProductoFormulacion;
